import { useQrLogin } from "@framework/auth/hooks";
import { trans } from "@mongez/localization";
import type QrScanner from "qr-scanner";
import { useEffect, useRef, useState } from "react";

/**
 * Own the camera lifecycle and direct-login state for the student-card scanner.
 *
 * @example
 * const scanner = useQrLoginScanner();
 */
export function useQrLoginScanner() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const processingRef = useRef(false);
  const login = useQrLogin();
  const loginRef = useRef(login);
  const [cameraError, setCameraError] = useState("");
  const [processing, setProcessing] = useState(false);

  loginRef.current = login;

  useEffect(() => {
    let disposed = false;

    const startScanner = async () => {
      if (!videoRef.current) return;

      try {
        const { default: QrScannerClass } = await import("qr-scanner");

        if (disposed || !videoRef.current) return;

        const scanner = new QrScannerClass(
          videoRef.current,
          async (result) => {
            if (processingRef.current) return;

            processingRef.current = true;
            setProcessing(true);
            scanner.stop();

            const loggedIn = await loginRef.current(result.data);

            if (!loggedIn && !disposed) {
              processingRef.current = false;
              setProcessing(false);
              await scanner.start();
            }
          },
          {
            preferredCamera: "environment",
            highlightScanRegion: true,
            highlightCodeOutline: true,
            returnDetailedScanResult: true,
          },
        );

        scannerRef.current = scanner;
        await scanner.start();
      } catch {
        if (!disposed) {
          setCameraError(trans("auth.qrCameraError"));
        }
      }
    };

    void startScanner();

    return () => {
      disposed = true;
      scannerRef.current?.stop();
      scannerRef.current?.destroy();
      scannerRef.current = null;
    };
  }, []);

  return {
    cameraError,
    processing,
    videoRef,
  };
}
